import { Auth } from '@aws-amplify/auth';
import type { FieldProps } from 'formik';
import { Field, Formik } from 'formik';
import type { MouseEvent } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import styled from 'styled-components';
import * as Yup from 'yup';

import { BrandContext } from '../context/BrandProvider';
import withHookedTheme from '../hocs/withHookedTheme';
import type { Theme } from '../theme/themes/ThemeTypes';
import ErrorLabel from '../ui/ErrorLabel';
import LoginLayout from '../ui/Layout/LoginLayout';
import LoginCard from '../ui/LoginCard';
import PasswordVisibilityIcon from '../ui/PasswordVisibilityIcon';
import ThemeAwareLogo from '../ui/ThemeAwareLogo';
import { max } from '../utils/breakpoints';
import {
  buildRedirectUrl as buildDomainRedirectUrl,
  getCurrentHostPrefix as getHostPrefix,
  hasRedirected,
} from '../utils/domainRedirect';
import ensureError from '../utils/ensureError';

const SignUpWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;

  ${max.md} {
    flex-direction: column;
  }

  .signup-button {
    flex-shrink: 0;

    ${max.md} {
      width: 100%;
      margin-top: 0px;
    }
  }
`;

const PasswordFieldWrapper = styled.div`
  position: relative;
`;

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 44px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme?.colors?.light ?? '#afb6b9'};
  background: transparent;
  border: 0;
  border-radius: 0 0.25rem 0.25rem 0;
  cursor: pointer;
  z-index: 2;

  &:hover,
  &:focus,
  &:active {
    color: ${(props) => props.theme?.colors?.primary ?? '#7c8589'};
    background: transparent;
    border: 0;
  }

  &:focus,
  &:active {
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
  }

  &:focus-visible svg {
    border-radius: 999px;
    box-shadow: 0 0 0 0.15rem
      ${(props) => (props.theme?.colors?.primary ? `${props.theme.colors.primary}40` : 'rgba(0, 123, 255, 0.25)')};
  }
`;

const PasswordInput = styled(Form.Control)`
  padding-right: 44px;
`;

interface CognitoSignInResult {
  challengeName?: string;
  challengeParam?: Record<string, unknown>;
  [key: string]: unknown;
}

type ChangeAuthState = (state: string, data?: unknown) => void;

interface LoopSignInProps {
  theme: Theme;
  authState?: string;
  changeState?: ChangeAuthState;
  onStateChange?: ChangeAuthState;
}

/**
 * Sign in screen. Talks to @aws-amplify/auth directly (no longer extends aws-amplify-react's SignIn).
 */
const LoopSignIn: React.FC<LoopSignInProps> = ({ theme, authState, changeState, onStateChange }) => {
  const brandCtx = useContext(BrandContext);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const initialVisit = useRef(true);

  const goToState = useCallback<ChangeAuthState>(
    (state, data) => {
      if (typeof onStateChange === 'function') {
        onStateChange(state, data);
      } else if (typeof changeState === 'function') {
        changeState(state, data);
      }
    },
    [onStateChange, changeState]
  );

  // First-time visitors land here by default; nudge them to sign up instead.
  useEffect(() => {
    if (window.location.pathname === '/' && initialVisit.current && authState === 'signIn') {
      const timer = setTimeout(() => {
        goToState('signUp');
        initialVisit.current = false;
      }, 0);
      return () => clearTimeout(timer);
    }
    if (authState === 'signedUp' || authState === 'confirmSignUp') {
      initialVisit.current = false;
    }
    return undefined;
  }, [authState, goToState]);

  if (authState !== 'signIn') return null;

  const signInValidationSchema = Yup.object({
    username: Yup.string().email('Invalid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const handleSignIn = async (
    values: { username: string; password: string },
    setSubmitting: (isSubmitting: boolean) => void,
    setStatus: (status: string | undefined) => void
  ): Promise<void> => {
    setSubmitting(true);
    setLoading(true);
    setStatus(undefined);
    const username = values.username.toLowerCase().trim();

    try {
      const user = (await Auth.signIn(username, values.password)) as CognitoSignInResult;

      // None of these challenge types are supported by this app (no MFA or admin-created-user flows
      // exist) and AuthController has no screens for them - sign the user back out and show an inline
      // error instead of transitioning to a state that would strand them on a blank page.
      if (
        user.challengeName === 'SMS_MFA' ||
        user.challengeName === 'SOFTWARE_TOKEN_MFA' ||
        user.challengeName === 'NEW_PASSWORD_REQUIRED' ||
        user.challengeName === 'MFA_SETUP' ||
        (user.challengeName === 'CUSTOM_CHALLENGE' && user.challengeParam?.trigger === 'true')
      ) {
        try {
          await Auth.signOut();
        } catch {
          // best-effort sign out
        }
        setStatus('Unable to sign in. Please contact support if this continues.');
        return;
      }

      // Amplify dispatches the Hub 'signIn' event (which AuthController listens for to run
      // verifiedContact and transition to 'signedIn'/'verifyContact', failing closed on error)
      // before Auth.signIn()'s promise resolves. Don't also call verifiedContact/goToState here -
      // running that check in two places races and can leave the user on a stale or wrong screen.

      // Only check the cross-domain redirect after a fully successful sign-in.
      const expectedPrefix = brandCtx?.domainPrefix;
      const currentPrefix = getHostPrefix();

      if (!expectedPrefix || brandCtx?.brandResolving) return;
      // If we've already been redirected (query param present), do not redirect again
      if (hasRedirected()) return;

      if (expectedPrefix !== currentPrefix) {
        await Auth.signOut();
        window.location.href = buildDomainRedirectUrl(expectedPrefix, '/login', { redirected: 'signIn' });
      }
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === 'UserNotConfirmedException') {
        goToState('confirmSignUp', { username });
      } else if (code === 'PasswordResetRequiredException') {
        // Cognito requires a new password but hasn't sent a reset code yet - request one before
        // showing the code-entry view, or the user would have no code to submit there.
        try {
          await Auth.forgotPassword(username);
        } catch {
          // Same non-enumerating behavior as LoopForgotPassword's send(): don't leak whether the
          // email is registered or expose Cognito's raw error - still proceed to the code entry step.
        }
        goToState('forgotPassword', { username });
      } else if (code === 'UserNotFoundException' || code === 'NotAuthorizedException') {
        // Don't disclose whether the account exists - both cases look the same to the user.
        setStatus('Incorrect username or password.');
      } else {
        setStatus(ensureError(err).message || 'Unable to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <LoginLayout>
      <Formik
        initialValues={{ username: '', password: '' }}
        validationSchema={signInValidationSchema}
        onSubmit={async (values, { setSubmitting, setStatus }) => {
          await handleSignIn(values, setSubmitting, setStatus);
        }}
        validateOnBlur
        validateOnChange
      >
        {({ handleSubmit, errors, isSubmitting, status }) => (
          <LoginCard>
            {theme.layout.showOrHideElements.auth.signIn.showCompanyLogoInCard && (
              <Row>
                <Col className="px-lg-3 px-md-2 px-1 d-flex justify-content-center mb-3">
                  <ThemeAwareLogo className="d-sm-block" alt="Company Logo" />
                </Col>
              </Row>
            )}
            <Card.Title as="h1" className="text-center">
              Sign in to
              <br />
              {theme.language.appName}
            </Card.Title>
            <Card.Text>
              {theme.language.appName} will buy and store energy when it's cheap, and sell it back to the grid when it's
              expensive.
            </Card.Text>
            {theme.layout.showOrHideElements.auth.signIn.howDoesOptimiseWorkLink && (
              <Card.Text>
                <a href={theme.links.howDoesItWork} rel="noreferrer" target="_blank">
                  How does {theme.language.appName} work?
                </a>
              </Card.Text>
            )}
            <Form noValidate onSubmit={handleSubmit} className="text-left">
              <Form.Group>
                <Form.Label htmlFor="username" className="form-label">
                  Email address
                </Form.Label>
                <Field name="username">
                  {({ field, meta }: FieldProps) => {
                    const statusMessage = typeof status === 'string' ? status : undefined;
                    const errorMsg = meta.error && meta.touched ? meta.error : statusMessage;
                    return (
                      <>
                        <Form.Control
                          {...field}
                          id="signin_username"
                          key="username"
                          type="email"
                          placeholder="Email address"
                          autoComplete="username"
                          onChange={(e) => {
                            e.target.value = e.target.value.toLowerCase().trim();
                            field.onChange(e);
                          }}
                          isInvalid={!!errorMsg}
                          style={{ backgroundImage: 'none' }}
                        />
                        {errorMsg && <ErrorLabel label={errorMsg} />}
                      </>
                    );
                  }}
                </Field>
              </Form.Group>
              <Form.Group>
                <Form.Label htmlFor="password" className="form-label">
                  Password
                </Form.Label>
                <Field name="password">
                  {({ field, meta }: FieldProps) => (
                    <>
                      <PasswordFieldWrapper>
                        <PasswordInput
                          {...field}
                          id="signin_password"
                          key="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Password"
                          isInvalid={meta.touched && !!meta.error}
                          style={{ backgroundImage: 'none' }}
                          autoComplete="current-password"
                        />
                        <PasswordToggleButton
                          type="button"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onMouseDown={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          <PasswordVisibilityIcon isPasswordVisible={showPassword} />
                        </PasswordToggleButton>
                      </PasswordFieldWrapper>
                      {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                    </>
                  )}
                </Field>
                <p className="mt-2">
                  <Button
                    className="pl-0"
                    variant="link"
                    onClick={() => {
                      goToState('forgotPassword', {});
                    }}
                  >
                    Forgot your password?{' '}
                  </Button>
                </p>
              </Form.Group>
              <Form.Group>
                <Button
                  type="submit"
                  variant="primary"
                  className="w-100"
                  disabled={loading || isSubmitting || !!Object.keys(errors).length}
                >
                  Sign In
                </Button>
              </Form.Group>
              <Form.Group className="mb-1">
                <p className="text-center">
                  Already have a sign in code?{' '}
                  <Button
                    className="p-0 align-baseline"
                    variant="link"
                    onClick={() => {
                      goToState('confirmSignUp');
                    }}
                  >
                    Verify code
                  </Button>
                </p>
              </Form.Group>
              <Form.Group className="mb-0 mt-1">
                <hr className="my-3" />
                <SignUpWrapper>
                  <span className="signup-text">Don&apos;t have a {theme.language.appName} account? </span>
                  <Button
                    type="button"
                    variant="outline-primary"
                    onClick={() => {
                      goToState('signUp');
                    }}
                    className="signup-button px-4"
                  >
                    Sign up
                  </Button>
                </SignUpWrapper>
              </Form.Group>
            </Form>
          </LoginCard>
        )}
      </Formik>
    </LoginLayout>
  );
};

export default withHookedTheme(LoopSignIn);
